import { useState } from "react";
import { SCAM_TYPES, HELP_SCENARIOS, SITUATIONS } from "@/data/situations";
import { ChevronRight, ShieldCheck, ShieldAlert } from "lucide-react";

export function SituationEngine({ onOpenInstant }) {
  return (
    <section id="engine" className="relative py-20 md:py-28 border-t border-white/5">
      <div className="absolute inset-0 tac-grid pointer-events-none opacity-30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">STEP 02 // SITUATION ENGINE</span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-4xl">
          Seven live scenario flows. Each ends in a clear <span className="text-lime-accent">next action</span>.
        </h2>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {SITUATIONS.map((s) => (
            <div key={s.id} className="glass rounded-md p-5" data-testid={`engine-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-xl font-bold uppercase">{s.label}</div>
                <button
                  className="btn-tactile ghost !py-1.5 !px-2 !text-[10px]"
                  onClick={() => onOpenInstant(s.id)}
                  data-testid={`engine-open-${s.id}`}
                >
                  Instant Mode <ChevronRight size={12} />
                </button>
              </div>
              <div className="text-xs text-[color:var(--lg-text-3)] mt-1 font-mono-tac tracking-widest">
                {s.subtitle.toUpperCase()}
              </div>
              <ol className="mt-4 space-y-1.5 text-sm text-[color:var(--lg-text-2)]">
                {s.right_now.slice(0, 4).map((step, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono-tac text-[10px] text-[color:var(--lg-cyan)] mt-0.5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        <ScamMini />
        <HelpSomeoneMini onOpenInstant={onOpenInstant} />
      </div>
    </section>
  );
}

function ScamMini() {
  const [type, setType] = useState(null);
  const [risk, setRisk] = useState(null);

  return (
    <div className="mt-16" id="scam-mini">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-amber)]">SCAM CHECK</span>
        <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-extrabold uppercase">What happened?</h3>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {SCAM_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => {
              setType(t);
              const heavy = t.toLowerCase().includes("otp") || t.toLowerCase().includes("payment") || t.toLowerCase().includes("bank");
              setRisk(heavy ? "high" : "moderate");
            }}
            data-testid={`scam-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className={`glass rounded-sm p-4 text-left hover:border-[color:var(--lg-amber)]/60 ${
              type === t ? "border-[color:var(--lg-amber)]/70 tac-glow-cyan" : ""
            }`}
          >
            <div className="font-display font-bold">{t}</div>
            <div className="mt-1 text-[10px] font-mono-tac tracking-[0.28em] text-[color:var(--lg-text-3)]">TAP TO ANALYSE</div>
          </button>
        ))}
      </div>
      {type && (
        <div className="mt-5 grid md:grid-cols-5 gap-4" data-testid="scam-result">
          <div className="md:col-span-2 glass rounded-md p-5">
            <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-amber)]">SCAM RISK CHECK (DEMO)</div>
            <div className="mt-2 font-display text-2xl font-extrabold uppercase">
              {risk === "high" ? "High risk" : "Moderate risk"}
            </div>
            <p className="mt-1 text-sm text-[color:var(--lg-text-2)]">Signal: “{type}”. Treat with caution.</p>
            <div className="mt-4 flex items-center gap-2">
              {risk === "high" ? (
                <ShieldAlert className="text-[color:var(--lg-red)]" />
              ) : (
                <ShieldCheck className="text-[color:var(--lg-cyan)]" />
              )}
              <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
                RULE-BASED · NOT A LEGAL DETERMINATION
              </span>
            </div>
          </div>
          <div className="md:col-span-3 grid grid-cols-5 gap-2">
            {[
              { l: "STOP", d: "Do not reply or click.", c: "var(--lg-red)" },
              { l: "DON'T SHARE", d: "No OTP, PIN, password.", c: "var(--lg-amber)" },
              { l: "VERIFY", d: "Call the number on your card.", c: "var(--lg-cyan)" },
              { l: "SECURE", d: "Change passwords now.", c: "var(--lg-lime)" },
              { l: "REPORT", d: "Notify bank & cybercrime line.", c: "var(--lg-cyan)" },
            ].map((s) => (
              <div key={s.l} className="glass rounded-sm p-3 col-span-5 sm:col-span-1">
                <div className="font-mono-tac text-[10px] tracking-[0.24em]" style={{ color: s.c }}>
                  {s.l}
                </div>
                <div className="mt-1 text-xs text-[color:var(--lg-text-2)]">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HelpSomeoneMini({ onOpenInstant }) {
  const [pick, setPick] = useState(HELP_SCENARIOS[0].id);
  const s = HELP_SCENARIOS.find((x) => x.id === pick);
  return (
    <div className="mt-16" id="help-someone">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-lime)]">HELP SOMEONE</span>
        <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
      </div>
      <h3 className="font-display text-2xl md:text-4xl font-extrabold uppercase max-w-3xl">
        You don&apos;t need to be a hero. You just need to know the <span className="text-lime-accent">next safe step</span>.
      </h3>
      <div className="mt-6 grid md:grid-cols-5 gap-4">
        <div className="md:col-span-2 space-y-2">
          {HELP_SCENARIOS.map((h) => (
            <button
              key={h.id}
              onClick={() => setPick(h.id)}
              className={`w-full text-left glass rounded-sm p-3 border ${
                pick === h.id ? "border-[color:var(--lg-lime)]/60" : "border-white/10"
              }`}
              data-testid={`help-pick-${h.id}`}
            >
              <div className="font-display font-bold uppercase">{h.label}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-3 glass rounded-md p-5" data-testid="help-detail">
          <div className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
            NEXT SAFE STEP · {s.label.toUpperCase()}
          </div>
          <ol className="mt-3 space-y-2">
            {s.steps.map((st, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-mono-tac text-[11px] text-[color:var(--lg-lime)] mt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{st}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4">
            <button className="btn-tactile primary" onClick={() => onOpenInstant("help")} data-testid="help-open-instant">
              Enter Instant Mode
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
